<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3
                class="font-weight-bold"
                v-text="isDeletePage() != true ? 'Maintenance Logs' : 'Deleted Maintenance Logs'"
              ></h3>
              <section v-if="isDeletePage() != true">
                <v-btn
                  class="mx-2 mb-2"
                  fab
                  x-small
                  color="primary"
                  to="/maintenance_logs/create"
                >
                  <v-icon dark> mdi-plus </v-icon>
                </v-btn>
              </section>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search..."
                    prepend-inner-icon="mdi-magnify"
                    hide-details="auto"
                    clearable
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    ref="status"
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    hide-details="auto"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-toggle-switch"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    ref="device"
                    solo
                    rounded
                    v-model="filter.device_id"
                    :items="deviceItems"
                    label="Device"
                    hide-details="auto"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-medical-bag"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    ref="technician"
                    solo
                    rounded
                    v-model="filter.technician_id"
                    :items="technicianItems"
                    label="Technician"
                    hide-details="auto"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-account-wrench"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title v-if="maintenance_logs.length != 0">
              <v-spacer></v-spacer>
              <v-btn
                rounded
                class="primary mx-4"
                @click="generateExcel()"
                v-if="isDeletePage() != true"
              >
                <span v-if="isExcelLoading"> Generating Excel... </span>
                <span v-else>Generate Excel</span>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <section v-if="isLoading" :style="`height: ${tableHeight}px`">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                :height="tableHeight"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left sr_no">Sr No</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">Device Name</th>
                      <th class="text-left">Technician</th>
                      <th class="text-left">Log Type</th>
                      <th class="text-left">Description</th>
                      <th class="text-left">Date & Time</th>
                      <!-- <th class="text-left">Parts Replaced</th> -->
                      <!-- <th class="text-left">Cost</th> -->
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="maintenance_logs.length != 0">
                    <tr v-for="(log, i) in maintenance_logs" :key="`log${i}`">
                      <td style="min-width: 85px">
                        {{ serialNoStarting + i }}
                      </td>
                      <td>
                        <v-tooltip bottom v-if="log.is_active == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="teal"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Active
                            </v-chip>
                          </template>
                          <span>Maintenance Log is Active</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="log.is_active == 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Inactive
                            </v-chip>
                          </template>
                          <span>Maintenance Log is InActive</span>
                        </v-tooltip>
                      </td>
                      <td>
                        {{ log.device && log.device.model_name ? log.device.model_name : "N/A" }}
                      </td>
                      <td>
                        {{ log.technician && log.technician.first_name ? log.technician.first_name + ' ' + (log.technician.last_name || '') : "N/A" }}
                      </td>
                      <td>
                        <v-chip
                          :color="getLogTypeColor(log.log_type)"
                          text-color="white"
                          small
                        >
                          <v-icon left small>{{ getLogTypeIcon(log.log_type) }}</v-icon>
                          {{ getLogTypeDisplayName(log.log_type) }}
                        </v-chip>
                      </td>
                      <td class="text-truncate" style="max-width: 200px">
                        {{ log.description || "N/A" }}
                      </td>
                      <td>{{ formatDateTime(log.created_at, null) }}</td>
                      <!-- <td>{{ log.parts_replaced || "None" }}</td> -->
                      <!-- <td>{{ log.cost ? '$' + log.cost : "N/A" }}</td> -->
                      <td class="text-right">
                        <template v-if="isDeletePage() != true">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto"
                                color="error"
                                @click="Delete_this(log.id)"
                              >
                                <v-icon dark> mdi-delete </v-icon>
                              </v-btn>
                            </template>
                            <span>Delete this maintenance log</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="ml-2"
                                fab
                                x-small
                                color="primary"
                                :to="`/maintenance_logs/${encryptIt(log.id)}`"
                              >
                                <v-icon dark> mdi-pencil </v-icon>
                              </v-btn>
                            </template>
                            <span>Edit this maintenance log</span>
                          </v-tooltip>
                        </template>
                        <template v-else>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto"
                                color="success"
                                @click="Restore_this(log.id)"
                              >
                                <v-icon dark> mdi-delete-restore </v-icon>
                              </v-btn>
                            </template>
                            <span>Restore this maintenance log</span>
                          </v-tooltip>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="10" class="text-center">No maintenance logs found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination v-model="page" :length="paginationLength">
              </v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="delete_log" max-width="500px">
        <v-card>
          <v-card-title class="headline">Delete Maintenance Log</v-card-title>
          <v-card-text>
            Are you sure you want to delete this maintenance log? This action cannot be undone.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="delete_log = false">
              Cancel
            </v-btn>
            <v-btn color="red darken-1" text @click="confirmDelete()">
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Restore Confirmation Dialog -->
      <v-dialog v-model="restore_log" max-width="500px">
        <v-card>
          <v-card-title class="headline">Restore Maintenance Log</v-card-title>
          <v-card-text>
            Are you sure you want to restore this maintenance log?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="restore_log = false">
              Cancel
            </v-btn>
            <v-btn color="green darken-1" text @click="confirmRestore()">
              Restore
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "MaintenanceLogsIndex",
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
        device_id: "",
        technician_id: "",
        show_deleted: false,
      },
      page: 1,
      paginationLength: 0,
      count: 0,
      delete_log: false,
      restore_log: false,
      selectedLogId: null,
      maintenance_logs: [],
      deviceItems: [],
      technicianItems: [],
      statuses: [
        { id: 0, text: "Select Active Status", value: "" },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],
      isLoading: false,
      isExcelLoading: false,
    };
  },
  computed: {
    serialNoStarting() {
      return (this.page - 1) * this.rowsPerPage + 1;
    },
    tableHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 250;
        case "sm":
          return 300;
        case "md":
          return 400;
        case "lg":
          return 500;
        case "xl":
          return 600;
        default:
          return 400;
      }
    },
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
    this.getMasters();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get(`masters?context=maintenance_log`);
        const masters = response.data;

        // Devices
        if (masters.devices && Array.isArray(masters.devices)) {
          masters.devices.forEach((device) => {
            this.deviceItems.push({
              id: device.id,
              text: `${device.model_name || device.serial_number} (${device.serial_number})`,
              value: device.id,
            });
          });
        }

        // Technicians
        if (masters.technicians && Array.isArray(masters.technicians)) {
          masters.technicians.forEach((technician) => {
            this.technicianItems.push({
              id: technician.id,
              text: `${technician.first_name} ${technician.last_name || ''}`,
              value: technician.id,
            });
          });
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
        if (error.response && error.response.status === 401) {
          //window.location.href = "/auth/login";
        }
      }
    },
    isDeletePage() {
      return this.$route.query.open_trash ? true : false;
    },
    async getData() {
      this.isLoading = true;
      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.filter.is_active = this.filter.is_active || "";

      let query = new URLSearchParams({
        page: this.page,
        rowsPerPage: this.rowsPerPage,
        show_deleted: this.filter.show_deleted,
        is_active: this.filter.is_active,
        search_keyword: this.filter.search_keyword,
        device_id: this.filter.device_id || "",
        technician_id: this.filter.technician_id || "",
      }).toString();

      try {
        const response = await axios.get(`maintenance_logs?${query}`);
        this.maintenance_logs = response.data.data;
        this.count = response.data.count;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response && error.response.status === 401) {
          //window.location.href = "/auth/login";
        }
      }
      this.isLoading = false;
    },
    Delete_this(id) {
      this.selectedLogId = id;
      this.delete_log = true;
    },
    async confirmDelete() {
      try {
        await axios.post(`maintenance_logs/delete/${this.encryptIt(this.selectedLogId)}`);
        this.delete_log = false;
        this.getData();
      } catch (error) {
        console.error("Error deleting:", error);
        if (error.response && error.response.status === 401) {
          //window.location.href = "/auth/login";
        }
      }
    },
    Restore_this(id) {
      this.selectedLogId = id;
      this.restore_log = true;
    },
    async confirmRestore() {
      try {
        await axios.post(`maintenance_logs/restore/${this.encryptIt(this.selectedLogId)}`);
        this.restore_log = false;
        this.getData();
      } catch (error) {
        console.error("Error restoring:", error);
        if (error.response && error.response.status === 401) {
          //window.location.href = "/auth/login";
        }
      }
    },
    async generateExcel() {
      this.isExcelLoading = true;
      try {
        const queryParams = new URLSearchParams({
          search_keyword: this.filter.search_keyword || "",
          is_active: this.filter.is_active || "",
          device_id: this.filter.device_id || "",
          technician_id: this.filter.technician_id || "",
        }).toString();

        const response = await axios.get(`maintenance_logs/excel?${queryParams}`, {
          responseType: "blob",
        });

        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "maintenance_logs.xlsx";
        link.click();
      } catch (error) {
        console.error("Error generating Excel:", error);
      } finally {
        this.isExcelLoading = false;
      }
    },
    getLogTypeDisplayName(logType) {
      const typeMap = {
        routine: "Routine",
        preventive: "Preventive",
        corrective: "Corrective",
        emergency: "Emergency",
        calibration: "Calibration",
        inspection: "Inspection",
        repair: "Repair",
        replacement: "Replacement",
      };
      return typeMap[logType] || logType;
    },
    getLogTypeIcon(logType) {
      const iconMap = {
        routine: "mdi-calendar-check",
        preventive: "mdi-shield-check",
        corrective: "mdi-wrench",
        emergency: "mdi-alert-circle",
        calibration: "mdi-tune",
        inspection: "mdi-magnify",
        repair: "mdi-hammer-wrench",
        replacement: "mdi-swap-horizontal",
      };
      return iconMap[logType] || "mdi-information";
    },
    getLogTypeColor(logType) {
      const colorMap = {
        routine: "primary",
        preventive: "success",
        corrective: "warning",
        emergency: "error",
        calibration: "info",
        inspection: "secondary",
        repair: "orange",
        replacement: "purple",
      };
      return colorMap[logType] || "secondary";
    },
    formatDateTime(date, time) {
      if (!date) return "N/A";
      
      // If time is explicitly null or we only have a datetime string, format the full datetime
      if (time === null || !time) {
        return new Date(date).toLocaleString();
      }
      
      // If we have separate date and time, format them separately
      const dateStr = new Date(date).toLocaleDateString();
      const timeStr = time || "N/A";
      return `${dateStr} ${timeStr}`;
    },
  },
};
</script>

<style scoped>
.striped-table >>> tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05);
}

.action_th {
  min-width: 120px;
}

.sr_no {
  min-width: 80px;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>